import { unsureContact, type AiMoment, type AiNotes, type Distance, type NoteKind, type Verdict } from './ai-notes';
import type { Moment } from './moments';
import type { Reason, ReasonKind } from './reasons';

export const SEVERE_BRAKE_MPS2 = 6;
export const NO_SIGNAL_TIER = 8;
export const FALSE_ALARM_TIER = 9;
const MOTION_TIER = 7;
const UNSURE_CONTACT_TIER = 3;

const REASON_TIERS: Readonly<Record<ReasonKind, number>> = {
  bump: 0,
  aeb: 1,
  handle: 3,
  manual: 4,
  honk: 4,
  object: MOTION_TIER,
  none: NO_SIGNAL_TIER,
  unknown: NO_SIGNAL_TIER,
};

const NOTE_TIERS: Readonly<Record<Exclude<NoteKind, 'approach'>, number>> = {
  damage: 0,
  near_miss: 2,
  emergency: 4,
  animal: 5,
  change: MOTION_TIER,
};

const APPROACH_TIERS: Readonly<Record<Distance, number>> = {
  touching: 1,
  under_1m: 3,
  '1_3m': 5,
  over_3m: 6,
};

const VERDICT_TIERS: Readonly<Record<Verdict, number>> = {
  incident: 3,
  notable: 5,
  routine: NO_SIGNAL_TIER,
  false_alarm: NO_SIGNAL_TIER,
};

export interface Rankable {
  readonly tier: number;
  readonly time: number;
}

export const reasonTier = (reason: Reason): number => REASON_TIERS[reason.kind];

export function momentTier(moment: Moment): number {
  if (moment.kind === 'brake') return moment.peak >= SEVERE_BRAKE_MPS2 ? 2 : 5;
  return moment.kind === 'disengage' ? 6 : 5;
}

export function noteTier(moment: AiMoment): number {
  if (unsureContact(moment)) return UNSURE_CONTACT_TIER;
  return moment.kind === 'approach' ? APPROACH_TIERS[moment.distance ?? 'over_3m'] : NOTE_TIERS[moment.kind];
}

const notesTiers = (notes: AiNotes | undefined): readonly number[] =>
  notes ? [notes.verdict ? VERDICT_TIERS[notes.verdict] : NO_SIGNAL_TIER, ...notes.moments.map(noteTier)] : [];

export function bestTier(reason: Reason | undefined, moments: readonly Moment[], notes?: AiNotes): number {
  const tier = Math.min(reason ? reasonTier(reason) : NO_SIGNAL_TIER, ...moments.map(momentTier), ...notesTiers(notes));
  return notes?.verdict === 'false_alarm' && tier >= MOTION_TIER ? FALSE_ALARM_TIER : tier;
}

export const byRank = (a: Rankable, b: Rankable): number => a.tier - b.tier || b.time - a.time;
